/**
 * Operator Feedback Analytics Page (Phase 21.9.4)
 *
 * Paginated table of all operator feedback across the organization.
 * Features:
 * - Filter by feedback type (assessment/training)
 * - Filter by sentiment (positive/negative)
 * - Anonymous enforcement (server-side, no names for anonymous feedback)
 * - Context column showing the SOP or training material title
 * - Standard pagination controls
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  ChevronLeft,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User,
  Filter,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import {
  useOperatorFeedbackList,
  type FeedbackListParams,
} from '../../hooks/useOperatorFeedbackAnalytics';

// ============================================
// CONSTANTS
// ============================================

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'assessment_fairness', label: 'Assessment Fairness' },
  { value: 'training_rating', label: 'Training Rating' },
] as const;

const SENTIMENT_OPTIONS = [
  { value: '', label: 'All Sentiments' },
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
] as const;

const PAGE_SIZE = 20;

// ============================================
// HELPERS
// ============================================

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatFeedbackType(type: string): string {
  return type === 'assessment_fairness' ? 'Assessment' : 'Training';
}

function getTypeBadgeClasses(type: string): string {
  return type === 'assessment_fairness'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-purple-100 text-purple-700';
}

// ============================================
// PAGE COMPONENT
// ============================================

export function OperatorFeedbackPage() {
  useDocumentTitle('Operator Feedback');
  const navigate = useNavigate();

  // Filter state
  const [feedbackType, setFeedbackType] = useState<FeedbackListParams['feedbackType']>('');
  const [sentiment, setSentiment] = useState<FeedbackListParams['sentiment']>('');
  const [page, setPage] = useState(1);

  // Fetch data
  const { data: response, isLoading } = useOperatorFeedbackList({
    page,
    limit: PAGE_SIZE,
    feedbackType,
    sentiment,
  });

  const items = response?.data ?? [];
  const pagination = response?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const total = pagination?.total ?? 0;

  // Reset to page 1 when filters change
  const handleTypeChange = (value: string) => {
    setFeedbackType(value as FeedbackListParams['feedbackType']);
    setPage(1);
  };

  const handleSentimentChange = (value: string) => {
    setSentiment(value as FeedbackListParams['sentiment']);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/analytics')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} />
            Analytics
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <MessageSquare size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Operator Feedback</h1>
              <p className="text-sm text-gray-500">
                Feedback from operators on assessments and training
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Filter size={14} />
          <span>Filters:</span>
        </div>

        <select
          value={feedbackType}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-guild-green focus:outline-none focus:ring-1 focus:ring-guild-green"
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={sentiment}
          onChange={(e) => handleSentimentChange(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-guild-green focus:outline-none focus:ring-1 focus:ring-guild-green"
        >
          {SENTIMENT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {(feedbackType || sentiment) && (
          <button
            onClick={() => {
              setFeedbackType('');
              setSentiment('');
              setPage(1);
            }}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                <MessageSquare className="h-7 w-7 text-gray-400" />
              </div>
              <p className="mt-3 text-sm font-medium text-gray-900">No feedback found</p>
              <p className="mt-1 text-sm text-gray-500">
                {feedbackType || sentiment
                  ? 'Try adjusting your filters.'
                  : 'Operator feedback will appear here once submitted.'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Operator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Sentiment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Context
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Comment
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    {/* Date */}
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {formatDate(item.createdAt)}
                    </td>

                    {/* Operator */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            'flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium',
                            item.isAnonymous
                              ? 'bg-gray-100 text-gray-400'
                              : 'bg-emerald-100 text-emerald-700'
                          )}
                        >
                          <User size={14} />
                        </div>
                        <span
                          className={cn(
                            'text-sm',
                            item.isAnonymous
                              ? 'italic text-gray-400'
                              : 'font-medium text-gray-900'
                          )}
                        >
                          {item.operatorName ?? 'Anonymous'}
                        </span>
                      </div>
                    </td>

                    {/* Type */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          getTypeBadgeClasses(item.feedbackType)
                        )}
                      >
                        {formatFeedbackType(item.feedbackType)}
                      </span>
                    </td>

                    {/* Sentiment */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        {item.sentiment === 'positive' ? (
                          <ThumbsUp
                            size={16}
                            className="text-emerald-600"
                            fill="currentColor"
                          />
                        ) : (
                          <ThumbsDown
                            size={16}
                            className="text-red-500"
                            fill="currentColor"
                          />
                        )}
                        <span
                          className={cn(
                            'text-sm font-medium capitalize',
                            item.sentiment === 'positive'
                              ? 'text-emerald-700'
                              : 'text-red-600'
                          )}
                        >
                          {item.sentiment}
                        </span>
                      </div>
                    </td>

                    {/* Context */}
                    <td className="max-w-[200px] truncate px-6 py-4 text-sm text-gray-600">
                      {item.context}
                    </td>

                    {/* Comment */}
                    <td className="max-w-[300px] px-6 py-4 text-sm text-gray-600">
                      {item.comment ? (
                        <span
                          className="line-clamp-2"
                          title={item.comment}
                        >
                          {item.comment}
                        </span>
                      ) : (
                        <span className="italic text-gray-300">No comment</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-3">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * pagination.limit + 1}
              {' '}-{' '}
              {Math.min(pagination.page * pagination.limit, total)} of {total}{' '}
              results
            </span>

            <div className="flex items-center gap-1">
              {/* First page */}
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 disabled:hover:bg-transparent"
                title="First page"
              >
                <ChevronsLeft size={16} />
              </button>

              {/* Previous page */}
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 disabled:hover:bg-transparent"
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              {/* Page indicator */}
              <span className="px-3 text-sm font-medium text-gray-700">
                {page} / {totalPages}
              </span>

              {/* Next page */}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 disabled:hover:bg-transparent"
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>

              {/* Last page */}
              <button
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 disabled:hover:bg-transparent"
                title="Last page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
